import { useState, useEffect, useRef } from 'react'
import { authenticatedFetchJson } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'

/**
 * Hook to handle live email sending progress via polling
 */
const useEmailSendingProgress = () => {
  const [isConnected, setIsConnected] = useState(false)
  const [progress, setProgress] = useState({
    status: 'idle',
    message: '',
    progress: 0,
    totalEmails: 0,
    emailDetails: []
  })
  const [emailStatuses, setEmailStatuses] = useState([])
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState(null)
  const pollingRef = useRef(null)

  const startEmailSending = async (automationId, testModeEmail = null) => {
    try {
      setIsConnected(false)
      setIsComplete(false)
      setError(null)
      setProgress({
        status: 'starting',
        message: 'Starting email sending process...',
        progress: 5,
        totalEmails: 0,
        emailDetails: []
      })
      setEmailStatuses([])

      setIsConnected(true)

      setProgress(prev => ({
        ...prev,
        status: 'analyzing',
        message: 'Analyzing campaigns and checking trigger conditions...',
        progress: 15
      }))

      const dryRunResults = await authenticatedFetchJson(apiEndpoints.automationTest(automationId), {
        method: 'POST',
        body: JSON.stringify({
          testMode: false
        })
      })

      const triggeredCampaigns = (dryRunResults.data || []).filter(r => r.would_trigger)
      
      setProgress(prev => ({
        ...prev,
        status: 'preparing',
        message: `Found ${triggeredCampaigns.length} campaigns that meet trigger threshold. Preparing to send emails...`,
        progress: 25,
        totalEmails: triggeredCampaigns.length
      }))

      if (triggeredCampaigns.length === 0) {
        setProgress(prev => ({
          ...prev,
          status: 'complete',
          message: 'No campaigns meet the trigger threshold',
          progress: 100
        }))
        setIsComplete(true)
        setIsConnected(false)
        return
      }

      setProgress(prev => ({
        ...prev,
        status: 'preparing',
        message: 'Preparing email sending for all campaigns...',
        progress: 30
      }))

      const initialStatuses = triggeredCampaigns.map(campaign => ({
        campaign_name: campaign.campaign_name,
        status: 'pending',
        success: false,
        recipients: 0,
        recipients_list: [],
        reason: null,
        error: null,
        timestamp: new Date().toISOString(),
        details: []
      }))

      setEmailStatuses(initialStatuses)

      setProgress(prev => ({
        ...prev,
        status: 'sending',
        message: 'Starting email sending process...',
        progress: 35
      }))

      for (let i = 0; i < triggeredCampaigns.length; i++) {
        const campaign = triggeredCampaigns[i]
        const currentProgress = 35 + ((i / triggeredCampaigns.length) * 55)
        
        setProgress(prev => ({
          ...prev,
          message: `Sending emails for ${campaign.campaign_name}... (${i + 1} of ${triggeredCampaigns.length})`,
          progress: currentProgress
        }))

        setEmailStatuses(prevStatuses => {
          const updated = [...prevStatuses]
          const campaignIndex = updated.findIndex(s => s.campaign_name === campaign.campaign_name)
          if (campaignIndex >= 0) {
            updated[campaignIndex] = {
              ...updated[campaignIndex],
              status: 'sending',
              timestamp: new Date().toISOString()
            }
          }
          return updated
        })

        try {
          const singleResult = await authenticatedFetchJson(apiEndpoints.automationTest(automationId), {
            method: 'POST',
            body: JSON.stringify({
              campaign_name: campaign.campaign_name,
              testMode: true,
              testModeEmail: testModeEmail
            })
          })

          const result = singleResult.data?.[0] || {}
          const hasEmailSent = result.test_mode?.email_sent || false
          const emailResults = result.test_mode?.email_results || []
          const success = hasEmailSent && emailResults.some(r => r.success)
          const recipientsList = result.test_mode?.original_recipients || []
          const actualRecipient = result.test_mode?.redirect_to || testModeEmail

          setEmailStatuses(prevStatuses => {
            const updated = [...prevStatuses]
            const campaignIndex = updated.findIndex(s => s.campaign_name === campaign.campaign_name)
            if (campaignIndex >= 0) {
              updated[campaignIndex] = {
                campaign_name: campaign.campaign_name,
                status: result.would_trigger 
                  ? (hasEmailSent ? (success ? 'sent' : 'failed') : 
                     (recipientsList.length > 0 ? 'failed' : 'no_recipients'))
                  : 'skipped',
                success: success,
                recipients: recipientsList.length,
                recipients_list: recipientsList,
                actual_recipient: actualRecipient,
                is_test_mode: true,
                reason: !result.would_trigger ? result.reason : 
                        (!hasEmailSent && recipientsList.length === 0) ? 'No recipients with email addresses' : null,
                error: !success && (hasEmailSent || recipientsList.length > 0) ? 'Failed to send email' : null,
                timestamp: new Date().toISOString(),
                details: emailResults
              }
            }
            return updated
          })

        } catch (campaignError) {
          
          setEmailStatuses(prevStatuses => {
            const updated = [...prevStatuses]
            const campaignIndex = updated.findIndex(s => s.campaign_name === campaign.campaign_name)
            if (campaignIndex >= 0) {
              updated[campaignIndex] = {
                campaign_name: campaign.campaign_name,
                status: 'failed',
                success: false,
                recipients: 0,
                recipients_list: [],
                reason: null,
                error: campaignError.message,
                timestamp: new Date().toISOString(),
                details: []
              }
            }
            return updated
          })
        }

        await new Promise(resolve => setTimeout(resolve, 300))
      }

      setEmailStatuses(prevStatuses => {
        const sentCount = prevStatuses.filter(s => s.status === 'sent').length
        const failedCount = prevStatuses.filter(s => s.status === 'failed').length
        const noRecipientsCount = prevStatuses.filter(s => s.status === 'no_recipients').length

        setTimeout(() => {
          setProgress({
            status: 'complete',
            message: `Email sending complete: ${sentCount} sent, ${failedCount} failed, ${noRecipientsCount} no recipients`,
            progress: 100,
            totalEmails: triggeredCampaigns.length,
            emailDetails: prevStatuses
          })
        }, 0)

        return prevStatuses
      })
      setIsComplete(true)
      setIsConnected(false)

    } catch (fetchError) {
      setError(fetchError.message)
      setIsConnected(false)
      setIsComplete(true)
    }
  }

  const disconnect = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    setIsConnected(false)
    setIsComplete(false)
    setError(null)
    setEmailStatuses([])
    setProgress({
      status: 'idle',
      message: '',
      progress: 0,
      totalEmails: 0,
      emailDetails: []
    })
  }

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [])

  return {
    startEmailSending,
    disconnect,
    isConnected,
    progress,
    emailStatuses,
    isComplete,
    error
  }
}

export default useEmailSendingProgress